// ---------------------------------------------------------------------------
// Témez model: soil reservoir balance + a linear aquifer — Témez, J.R.
// (1977), "Modelo matemático de transformación precipitación-aportación",
// ASINEL, Madrid.
//
// Formulation confirmed by decompiling EvalHid's real calculation engine
// (Evalhid.dll, .NET 6, via ilspycmd — see class Temez.calculo_temez()), not
// just against the Technical Manual: there are two real differences between
// what the manual documents and what the .exe actually calculates —
//
// 1. Aquifer recession uses V_new = V·e^(−α) + I·e^(−α/2) (the step's own
//    infiltration is treated as if it entered halfway through the step,
//    with only half a period of decay) — not the mass-balance form the
//    Manual/CEDEX-SIMPA documents.
// 2. Soil moisture (H) isn't clamped to Hmax after adding P — the .exe
//    doesn't have that clamp, so it isn't added here either (for fidelity,
//    even though the math formulation itself already keeps H≤Hmax for
//    physically reasonable inputs).
//
// Warning: with H (deliberately, see above) never clamped to Hmax, the
// surplus formula's denominator (`P + ETP + (Hmax−H)(1−2C)`) can go to
// zero or negative if H ever ends up above Hmax (e.g. an initial state
// entered/imported above Hmax) with C < 0.5 — well within the 0–1
// calibration range (C≥0.5 is always safe here: (Hmax−H)(1−2C) is then
// never negative, whatever H is) — producing Infinity/NaN that then
// "sticks" and contaminates every following step, exactly the same class
// of risk snow2.ts documents for its own division. Same deliberate safety
// deviation as there: this one division falls back to the always-safe,
// non-negative `P−P0` (the branch is only reached once P already exceeds
// P0) instead of dividing by a non-positive denominator.
//
// A second, similar deliberate deviation: `Temez.cs` computes
// `infilt = imax * (ti / (ti + imax))` unconditionally — with a dry step
// (`ti=0`, i.e. `surplus=0`) AND `imax=0` (infiltration to the aquifer
// disabled, a plausible calibration value), that's `0/0=NaN`, which then
// contaminates `esc_sup`/`vi`/`esc_subt` for the rest of the simulation
// (confirmed against the real source, 2026-08-25 audit — not replicated).
// `infiltration` below falls back to the always-correct `0` when
// `surplus===0`, whatever `imax` is, instead of dividing.
//
// The aquifer (V) is ALWAYS calculated, whether or not the sub-basin has a
// real aquifer assigned — but `contribution` only includes the underground
// component when `hasAquifer` is false. Historically the .exe checked
// `cod_acuifero == "-1"`, a sentinel that never appears in real data (the
// real CSV writes "0" for "no aquifer"), making the check dead code and the
// underground component always counted — confirmed fixed in the real
// source (2026-08, see CLAUDE.md's engine-source audit): `Temez.cs` now
// checks `cod_acuifero == "0"` and, with a real aquifer assigned, excludes
// the underground/base-flow component from the sub-basin's own
// contribution (it's assumed to flow to the aquifer's own aggregate
// balance instead — see aggregation.ts).
//
// Pumping (`bomb`, mm/step): subtracted from the infiltration BEFORE the
// aquifer recession, and again from the underground contribution itself
// (`Temez.cs`: `vi = Max(0, vo·e^-α + (infilt-bomb)·e^-α/2)`,
// `esc_subt = Max(0, vo-vi+infilt-bomb)` — both floored at 0, unlike the
// no-pumping form this port had before). Resolved from the sub-basin's own
// `Ent_Bomb.csv` column (`SubBasin.pumpingCol`, by name, independent of its
// aquifer's own pumping) and converted from the raw CSV volume to mm/step
// (`engine.ts`'s `subBasinPumpingDepth`, mirroring
// `SeriesTemporales.cs`'s `actualiza_bomb`) before reaching here — `null`
// only when the sub-basin has no pumping column of its own, or "Modelar
// bombeos" is unchecked (`CalculationOptions.modelPumping`).
// ---------------------------------------------------------------------------

export interface TemezParameters {
  /** Hmax (mm): maximum soil storage capacity. */
  hmax: number;
  /** C (dimensionless): surplus onset coefficient. */
  c: number;
  /** Imax (mm): maximum infiltration into the aquifer per time step. */
  imax: number;
  /** Alfa (1/time step): aquifer depletion coefficient. */
  alfa: number;
}

interface TemezState {
  /** Ho (mm): initial soil moisture. */
  ho: number;
  /** Vo (mm): initial aquifer storage. */
  vo: number;
}

interface TemezDetail {
  [key: string]: Float64Array;
  /** H (mm): soil moisture after the step. */
  soilMoisture: Float64Array;
  /** Etr (mm): actual evapotranspiration (bounded by available moisture, not always the potential ETP). */
  etr: Float64Array;
  /** Infiltration into the aquifer (mm). */
  infiltration: Float64Array;
  /** V (mm): aquifer storage after the step. */
  aquifer: Float64Array;
  /** Surface runoff (mm). */
  surfaceRunoff: Float64Array;
  /** Groundwater runoff/base flow (mm). */
  undergroundRunoff: Float64Array;
}

interface TemezResult {
  /** Total contribution (surface + groundwater) per time step, mm. */
  contribution: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: TemezDetail;
}

/**
 * Simulates the Témez model for a single sub-basin. `precipitation` and
 * `etp` must already come adjusted (precipAdjustment/etpAdjustment applied) and aligned step
 * by step.
 */
export function calculateTemez(
  precipitation: Float64Array,
  etp: Float64Array,
  parameters: TemezParameters,
  initialState: TemezState,
  hasAquifer: boolean,
  pumping: Float64Array | null
): TemezResult {
  const { hmax, c, imax, alfa } = parameters;
  const n = precipitation.length;
  const contribution = new Float64Array(n);
  const detail: TemezDetail = {
    soilMoisture: new Float64Array(n),
    etr: new Float64Array(n),
    infiltration: new Float64Array(n),
    aquifer: new Float64Array(n),
    surfaceRunoff: new Float64Array(n),
    undergroundRunoff: new Float64Array(n),
  };

  let h = initialState.ho;
  let v = initialState.vo;

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    const potentialEtp = Number.isFinite(etp[t]) ? etp[t] : 0;

    const delta = hmax - h + potentialEtp;
    const p0 = c * (hmax - h);
    const denominator = p + delta - 2 * p0;
    // See header note 2's warning: denominator is only ever <= 0 when H has
    // drifted above Hmax (H is never clamped there, by design) — the P−P0
    // fallback is always safe here since this whole branch already implies
    // p > p0.
    let surplus: number;
    if (p <= p0) {
      surplus = 0;
    } else if (denominator > 0) {
      surplus = (p - p0) ** 2 / denominator;
    } else {
      surplus = p - p0;
    }

    const infiltration = surplus === 0 ? 0 : (imax * surplus) / (surplus + imax);
    const surfaceContribution = surplus - infiltration;

    const bomb = pumping && Number.isFinite(pumping[t]) ? pumping[t] : 0;
    const nextV = Math.max(0, v * Math.exp(-alfa) + (infiltration - bomb) * Math.exp(-alfa / 2));
    const undergroundContribution = Math.max(0, v - nextV + infiltration - bomb);

    contribution[t] = hasAquifer
      ? surfaceContribution
      : surfaceContribution + undergroundContribution;

    const actualEtr = Math.min(h + p - surplus, potentialEtp);
    h = Math.max(0, h + p - surplus - potentialEtp);
    v = nextV;

    detail.soilMoisture[t] = h;
    detail.etr[t] = actualEtr;
    detail.infiltration[t] = infiltration;
    detail.aquifer[t] = v;
    detail.surfaceRunoff[t] = surfaceContribution;
    detail.undergroundRunoff[t] = undergroundContribution;
  }

  return { contribution, detail };
}
